const R = require('ramda');
const { uniq, failProofAssign } = require('../utils');
const sumMetric = (acc, { metric }) => sumObject(acc, metric);
const { STATUS_PRIORITY } = require('../constants');
const {
  sumObject,
  mergeReports,
  mergeReportArray,
  getMetricFromList,
  mergeActions,
  mergeConversionsByTracker,
  groupBySubChannel,
  getListMinTime,
  getListMaxTime,
  getTrackerConversions,
  attachClickAndSpendToConversions,
} = require('../utils/utils');
const utils = require('../utils/utils');
const ArchitectureElement = require('./ArchitectureElement');
const { getMetricAndConversionFromReport } = require('../utils/metricUtils');

/** @type {Campaign} */
class Campaign extends ArchitectureElement {
  constructor(obj) {
    super();
    /** @type {string} The name of the campaign. */
    this.name;
    /** @type {'RUNNING'|'PAUSED'|'DELETED'|'IN_REVIEW'|'DISAPPROVED'|'UNKNOWN'}
     * The status of the campaign.*/
    this.status;
    /** @type {string} unique id for this campaign among all architecture element ids. ex: 'facebook:campaign:123123' */
    this.id;
    /** @type {string} The id from the channel. Format depends on channels.*/
    this.channelId;
    /** @type {string} The date when the campaign was created (before being started).
     * Format: 'YYYY-MM-DD'.*/
    this.createdAt;
    /** @type {string} The date when the campaign was started for the first time.
     * Format: 'YYYY-MM-DD'.
     * Null if the campaign was never started. */
    this.startedAt;
    /** @type {string} The date when the campaign was stopped for the last time.
     * Format: 'YYYY-MM-DD'.
     * Null if the campaign was never stopped.
     */
    this.stoppedAt;
    /** @type {string[]} 'google', 'facebook', ...*/
    // this.channels
    /** @type {string} Objective of the campaign. For ex, link clicks, or conversions. */
    this.objective;
    this.updateData(obj);
  }

  updateData(obj) {
    if (!obj) return;
    obj.channelStatus = obj.status;
    failProofAssign(this, R.omit(['dailyBudget', 'status', 'channel', 'metric'], obj));
  }

  get level() {
    return 'campaign';
  }

  // get status() {
  //     if (this.channelStatus !== 'RUNNING') return this.channelStatus;
  //     const adSetStatuses = this.adSets.map(R.prop('status'));
  //     if (adSetStatuses.length === 0) return 'PAUSED';
  //     for (const status of R.reverse(STATUS_PRIORITY)) {
  //         if (adSetStatuses.some(R.equals(status))) return status;
  //     }
  //     return this.channelStatus;
  // }

  get channels() {
    const channel = this.channel;
    if (channel === 'facebook') {
      if (this.subChannels && this.subChannels.length > 0) {
        return this.subChannels.filter(subChannel => ['facebook', 'instagram'].includes(subChannel));
      }
    }
    return [channel];
  }

  isChannel(channel) {
    return this.channel == channel;
  }

  get isConversion() {
    return ['CONVERSIONS', 'APP_INSTALLS'].includes(this.objective);
  }

  get resultIndicator() {
    const OBJECTIVE_TO_RESULT_INDICATOR = {
      LINK_CLICKS: 'clicks',
      APP_INSTALLS: 'installs',
      LEAD_FORM: 'leads',
    };
    if (OBJECTIVE_TO_RESULT_INDICATOR[this.campaign.objective]) {
      return OBJECTIVE_TO_RESULT_INDICATOR[this.campaign.objective];
    }
    let conversionGoals = uniq(this.adSets.map(adSet => adSet.conversionGoal));
    if (conversionGoals.length == 1) return conversionGoals[0];
    else if (conversionGoals.length > 1) return 'multiple';
    else return 'clicks';
  }

  get subChannels() {
    if (this._subChannels && !this.isFacebook) return this._subChannels;

    if (this.adSets && this.adSets.length > 0) {
      const subChannels = uniq(this.adSets.flatMap(adSet => adSet.subChannels).filter(Boolean)).sort();
      if (subChannels.length > 0) return subChannels;
    }
    let subChannelReports = this.architecture.reportsByIdByDetail['subChannel'][this.id];
    if (subChannelReports && subChannelReports.length > 0) {
      let subChannels = uniq(subChannelReports.map(r => r.text)).sort();
      if (subChannels.length > 0) return subChannels;
    }
    return [this.channel];
  }

  set subChannels(subChannels) {
    this._subChannels = subChannels;
  }

  getImage() {
    if (this.image) return this.image;
    return null;
  }

  // get reports() {
  //   if(this._reports && this._reports.length>0) return this._reports
  //   return this.ads.map(ad => ad.report).filter(Boolean);
  // }

  // set reports(reports){
  //   this._reports = reports
  // }

  get children() {
    return this.adSets;
  }

  // get metric() {
  //   return getMetricFromList(this.adSets);
  // }

  get adSpent() {
    return this.metric.spend;
  }

  get dailyBudget() {
    let dailyBudget = null;
    if (this.budget && !this.budget.isTotalSum) {
      dailyBudget = this.budget.amount;
    }
    if (dailyBudget == null && this.adSets.find(adSet => adSet.budget)) {
      dailyBudget = this.adSets
        .filter(adSet => adSet.status === 'RUNNING' && adSet.budget && adSet.budget.amount != null)
        .reduce((acc, adSet) => acc + adSet.budget.amount, 0);
    }
    return dailyBudget;
  }

  get dailyAvailableBudget() {
    if (this.status !== 'RUNNING') return 0;
    return this.dailyBudget;
  }

  get dailyBudgetIncludingFees() {
    return this.dailyBudget * (1 + this.serviceFee / 100);
  }

  get fees() {
    if (this.isCustom) return 0;
    return (this.adSpent * this.serviceFee) / 100;
  }

  get campaign() {
    return this;
  }

  get serviceFee() {
    if (this.isCustom) return 0;
    return this.adrielCampaign.service_fee;
  }

  get isCustom() {
    return this.adAccount.external || !this.adrielCampaign;
  }

  get actions() {
    const adSetsActions = R.compose(mergeActions, R.flatten, R.map(R.prop('actions')))(this.adSets);
    return adSetsActions;
  }

  get conversions() {
    return R.compose(mergeActions, R.filter(Boolean), R.map(R.prop('conversion')))(this.adSets);
  }

  getConversionsSummaryByTrackerPerDate({ dateGroup } = {}) {
    let conversionByTrackerPerDates = this.getConversionsByTrackerPerDate({
      dateGroup,
    });
    if (!conversionByTrackerPerDates || R.isEmpty(conversionByTrackerPerDates))
      conversionByTrackerPerDates = this.adSets
        .map(adSet => adSet.getConversionsSummaryByTrackerPerDate())
        .flat()
        .filter(Boolean);
    return utils.mergeConversionsByTrackerPerDate(conversionByTrackerPerDates);
  }

  get chosenConversions() {
    return uniq(
      this.adSets
        .map(adSet => adSet.conversionGoal)
        .flat()
        .filter(Boolean),
    );
  }

  get isAppInstall() {
    return this.objective === 'APP_INSTALLS';
  }

  getDimensionElementIdByDimensionId() {
    const dimensionElementIdByDimensionId =
      this.architecture.dimensionElementIdByDimensionIdByCampaignId[this.id] || {};
    return dimensionElementIdByDimensionId;
  }

  getDimensionElements(dimensionIds) {
    const dimensionElementIdByDimensionId = this.getDimensionElementIdByDimensionId();
    const dimensionElementIds = dimensionIds.map(id => dimensionElementIdByDimensionId[id]).filter(Boolean);
    const dimensionElements = dimensionElementIds.map(id => this.architecture.dimensionElementById[id]).filter(Boolean);
    return dimensionElements;
  }

  getDimensionIds() {
    return Object.keys(this.getDimensionElementIdByDimensionId());
  }

  getSchedule() {
    if (this.adrielCampaign) return this.adrielCampaign.getSchedule();
    const schedules = this.adSets.map(adSet => adSet.schedule).filter(Boolean);
    if (schedules.length === 0) return null;
    return utils.mergeSchedules(schedules);
  }

  get startDate() {
    //TODO: does not work anymore
    const date = R.compose(
      R.ifElse(R.isEmpty, R.always(null), getListMinTime),
      R.filter(Boolean),
      R.map(R.path(['schedule', 'startDate'])),
    )(this.adSets);
    if (!date && this.adrielCampaignId) {
      return this.adrielCampaign.startedDate;
    }
    return date;
  }

  get isContinuously() {
    return this.adSets.some(R.path(['schedule', 'continuously']));
  }

  get endDate() {
    if (this.isContinuously) return null;
    const date = R.compose(getListMaxTime, R.filter(Boolean), R.map(R.path(['schedule', 'endDate'])))(this.adSets);
    if (!date && this.adrielCampaignId) {
      return this.adrielCampaign.endDate;
    }
    return date;
  }

  getName() {
    return this.adrielCampaign ? this.adrielCampaign.title : this.name;
  }

  hasPixel() {
    if (this.adrielCampaign) {
      return this.adrielCampaign.hasPixel();
    }
    return true;
  }

  hasGA() {
    if (this.adrielCampaign) {
      return this.adrielCampaign.hasGA();
    }
    return false;
  }

  // get subChannels() {
  //   return uniq(this.adSets.map(adSet=>adSet.subChannels))
  // }

  // TODO: merge in getReports & remove
  getKeywordsObj() {
    const adSetKeywords = R.flatten(
      this.adSets
        // .filter(({ searchKeywords }) => searchKeywords)
        .map(adSet => adSet.getReports({ detail: 'searchKeywords' }))
        .filter(Boolean),
    );
    const keywords = (this.searchKeywords || []).concat(adSetKeywords);
    if (keywords.length === 0) return null;
    return R.reduceBy(sumMetric, {}, R.prop('text'), keywords);
  }

  // TODO: merge in getReports & remove
  getDetailsObj(detailName) {
    const adSetDetails = R.flatten(
      // this.adSets.filter(adSet => adSet[detailName]).map(adSet => adSet[detailName]),
      this.adSets.filter(adSet => adSet[detailName]).map(adSet => adSet.getReports({ detail: detailName })),
    );
    const details = (this[detailName] || []).concat(adSetDetails);
    if (details.length === 0) return null;
    return R.reduceBy(sumMetric, {}, R.prop('text'), details);
  }

  // TODO: merge in getReports & remove
  getDetails(detailName) {
    const detailsObj = this.getDetailsObj(detailName);
    if (!detailsObj) return null;
    return Object.entries(detailsObj).map(([text, metric]) => ({
      text,
      metric,
    }));
  }

  // TODO: merge in getReports & remove
  getKeywords() {
    const keywordsObj = this.getKeywordsObj();
    if (!keywordsObj) return null;
    return Object.entries(keywordsObj).map(([text, metric]) => ({
      text,
      metric,
    }));
  }

  getActionsWithSpend() {
    const adSetsActions = R.compose(
      mergeActions,
      R.flatten,
      R.map(adSet => {
        return adSet.getActionsWithSpend();
      }),
    )(this.adSets);
    return adSetsActions;
  }

  replaceLeadFormValueFromAdrielCampaign(conversions) {
    if (this.isAdrielLeadForm) {
      const campaignMetric = this.metric;
      //ignore pixel lead event when campaign is adriel campaign
      if (conversions.pixel) {
        const leadEvent = conversions.pixel.find(trackerEvt => trackerEvt.name === 'lead');
        if (leadEvent) {
          leadEvent.value = campaignMetric.leads;
          leadEvent.spend = campaignMetric.spend;
        }
      }
    }
    return conversions;
  }

  getAllCampaignConversions() {
    // if campaign is adriel campaign and goal is lead form

    let conversions = getTrackerConversions(this.actions);
    const adSetClicks = this.metric.clicksOnActions;
    const adSetSpends = this.metric.spendOnActions;
    conversions = this.replaceLeadFormValueFromAdrielCampaign(conversions);
    return attachClickAndSpendToConversions(conversions, {
      click: adSetClicks,
      spend: adSetSpends,
    });
  }

  get bidCap() {
    return this.bidAmount;
  }

  get objectives() {
    return [this.objective];
  }

  get bidStrategies() {
    if (this.bidStrategy) return [this.bidStrategy];
    return null;
  }

  // format() {
  //     return {
  //         adSets: this.adSets.map((adSet) => adSet.format()),
  //         campaignId: this.channelId,
  //         ...R.pick(
  //             [
  //                 'id',
  //                 'channelId',
  //                 'name',
  //                 'subChannels',
  //                 'objectives',
  //                 'active',
  //                 'channelStatus',
  //                 'status',
  //                 'schedule',
  //                 'metric',
  //                 'bidCap',
  //                 'bidStrategies',
  //                 'placements',
  //                 'projectId',
  //             ],
  //             this
  //         ),
  //         budget: this.formattedBudget,
  //         adAccountName: this.adAccount ? this.adAccount.name : null,
  //         conversionEvents: this.chosenConversions,
  //         conversionsByTracker: this.getConversionsByTracker(),
  //         kpi: this.getKPI(),
  //     };
  // }
  // getConversionGoalMap() {
  //   const keyNameSet = this.adSets.map(adSet => adSet.getConversionGoalMap());
  //   return utils.unionNameToKeyMap(keyNameSet);
  // }

  getJSON() {
    const adSetIds = this.getChildElementIds();
    const obj = R.omit(['adrielCampaign', 'adSets', 'adAccount', 'architecture', 'FEFormat', '_subChannels'], this);
    obj.hasDisapprovedAds = this.hasDisapprovedAds();
    obj.effectiveStatus = this.getEffectiveStatus();
    obj.dailyBudget = this.dailyBudget;
    obj.adrielCampaignId = this.adrielCampaign && this.adrielCampaign.id;
    obj.subChannels = this.subChannels;
    obj.channel = this.adAccount.channel;
    obj.status = this.status;
    obj.metric = this.metric;
    obj.conversionsByTracker = this.getConversionsByTracker();
    obj.adSetIds = adSetIds;

    obj.currency = this.adAccount.currency;
    obj.external = this.adAccount.external;
    let getFormattedReports = reports => {
      return reports.map(report => getMetricAndConversionFromReport({ report, element: this }));
    };
    obj.reportsPerMonth = getFormattedReports(this.getReports({ dateGroup: 'month' }));
    obj.reportsPerWeek = getFormattedReports(this.getReports({ dateGroup: 'week' }));
    obj.reportsPerDay = getFormattedReports(this.getReports({ dateGroup: 'day' }));
    obj.level = this.level;

    return obj;
  }
}

module.exports = Campaign;
